/** @jsx jsx */
// External libraries
import { useEffect } from 'react';
import { jsx } from '@emotion/core';
import { appStore, useStoreState } from '@/store';
import { getContent, getLocale, logger } from '@/lib';

//  Internal modules
import { getTermsLinks } from './Components';
import { useMountLogging } from '@/lib/reactHooks';
import { getBillingAgreementKey, getMerchantName } from './utils';
import { textStyles } from './styles';
import { StyledText } from '@/components/Common';
import { CheckoutSessionType } from '@/generated/graphql';
import type { BillingType } from '@/generated/graphql';

export const BillingTerms = ({
  billingType,
}: {
  billingType: BillingType;
}): React.ReactElement => {
  useMountLogging(`${billingType}BillingTerms`);
  const { cartDetails, isBillingWithoutPurchase } = appStore.getState().api;
  const accountType = useStoreState(({ form }) => form.onboardOption.value);
  const locale = getLocale();
  const fiType = useStoreState(({ ui }) => ui.fiType);
  const billingAgreementKey = getBillingAgreementKey(
    accountType,
    billingType,
    fiType,
  );

  useEffect(() => {
    logger.fpti(
      'guest_signup_billing_agreement_terms_content_decision_occurred',
      {
        account_type: accountType,
        billing_intent: isBillingWithoutPurchase
          ? CheckoutSessionType.BillingWithoutPurchase
          : CheckoutSessionType.BillingWithPurchase,
        billing_type: billingType,
        event_name:
          'guest_signup_billing_agreement_terms_content_decision_occurred',
        locale,
        state_name: 'guest_signup_billing_agreements',
        terms_key: billingAgreementKey,
        transition_name: 'guest_signup_billing_agreement_terms',
      },
    );
  }, [
    accountType,
    billingAgreementKey,
    billingType,
    isBillingWithoutPurchase,
    locale,
  ]);

  return (
    <StyledText css={textStyles} data-testid="billing-terms-container">
      {getContent(billingAgreementKey, {
        billPayIntegratorName: (
          <strong>{cartDetails?.merchant?.billPayIntegratorName}</strong>
        ),
        merchantName: <strong>{getMerchantName(cartDetails)}</strong>,
        ...getTermsLinks(billingAgreementKey),
      })}
    </StyledText>
  );
};
